import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CalendarClock, ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox, DataTable, DateField, EmptyState, ErrorState, FormField, IconButton, PageHeader, SearchPanel, StatusBadge, TextField, type Column } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters';
import { BulkReportButton } from '@/features/report-jobs/BulkReportButton';
import { weekdayJa } from '@/lib/wareki';
import { dms, PAGE_SIZE, useFixedReports, type FixedListFilter, type FixedReportDto } from './api';
import '@/features/advance-notices/advance-notices.css';
import '@/features/voyage-reports/voyage-reports.css';

const KEYS = ['businessOperatorId', 'vesselName', 'dateFrom', 'dateTo', 'hasSighting', 'hasGearFinding', 'page'] as const;

function useFilterFromUrl(): [FixedListFilter, (f: FixedListFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<FixedListFilter>(() => {
    const f: Record<string, string | number> = {};
    KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) f[k] = k === 'businessOperatorId' || k === 'page' ? Number(v) : v;
    });
    return f as FixedListFilter;
  }, [params]);
  const setFilter = (f: FixedListFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v !== '' && v != null && v !== false && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

/** S-30 定時連絡一覧（定時検索・編集）。作業概要・作業予定は行を展開して表示 */
export function FixedReportListPage() {
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useFixedReports(year, filter);
  const { register, handleSubmit, reset, control } = useForm<FixedListFilter & { sOnly: boolean; gOnly: boolean }>({
    values: { ...filter, sOnly: filter.hasSighting === 'true', gOnly: filter.hasGearFinding === 'true' },
  });
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setOpen((s) => new Set(s.has(id) ? [...s].filter((x) => x !== id) : [...s, id]));

  const columns: Column<FixedReportDto>[] = [
    {
      key: 'expand',
      header: '',
      width: 36,
      render: (r) => (
        <IconButton
          size="sm"
          label={open.has(r.id) ? '作業概要を閉じる' : '作業概要を開く'}
          icon={open.has(r.id) ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          onClick={(e) => {
            e.stopPropagation();
            toggle(r.id);
          }}
        />
      ),
    },
    {
      key: 'reportDate',
      header: '年月日・時刻',
      width: 120,
      render: (r) => (
        <>
          <span className="bs-num">
            {r.reportDate.replaceAll('-', '/')}（{weekdayJa(r.reportDate)}）
          </span>
          <span className="bs-cell-sub bs-num">{r.reportTime ?? ''}</span>
        </>
      ),
    },
    { key: 'businessOperatorName', header: '事業実施者', width: 150 },
    {
      key: 'vesselName',
      header: '船名',
      render: (r) => (
        <>
          {r.vesselName}
          <span className="bs-cell-sub bs-num">{r.registrationNo}</span>
        </>
      ),
    },
    {
      key: 'hasSighting',
      header: '視認・漁具',
      width: 84,
      render: (r) => (
        <span style={{ display: 'flex', gap: 4 }}>
          {r.hasSighting && <StatusBadge tone="accent">視認</StatusBadge>}
          {r.hasGearFinding && <StatusBadge tone="warning">漁具</StatusBadge>}
          {!r.hasSighting && !r.hasGearFinding && <span className="bs-cell-sub">—</span>}
        </span>
      ),
    },
    {
      key: 'latDeg',
      header: '位置・番号',
      width: 136,
      render: (r) => (
        <span className="bs-num">
          N{dms(r.latDeg, r.latMin, r.latSec)} E{dms(r.lonDeg, r.lonMin, r.lonSec)}
          <span className="bs-cell-sub">{r.zoneNo ?? ''}</span>
        </span>
      ),
    },
    { key: 'course', header: '針路/速力', width: 92, render: (r) => [r.course, r.speed].filter(Boolean).join(' / ') },
    { key: 'weather', header: '天候/気圧', width: 100, render: (r) => [r.weather, r.pressure].filter(Boolean).join(' / ') },
    { key: 'windDirection', header: '風向/風力', width: 84, render: (r) => [r.windDirection, r.windForce].filter(Boolean).join(' / ') },
    { key: 'waves', header: '風浪/うねり', width: 92, render: (r) => [r.waves, r.swell].filter(Boolean).join(' / ') },
    { key: 'visibility', header: '視程', width: 60 },
  ];

  const items = data?.items ?? [];
  return (
    <>
      <PageHeader
        eyebrow="日々の報告"
        title="定時連絡"
        description="正午の定時連絡（送信書 別記様式第2-1号）の検索・編集。新規登録は「事前連絡」一覧の各行から行います。"
        actions={<BulkReportButton reportId="R-03" params={Object.fromEntries(Object.entries({ ...filter, page: undefined }).filter(([, v]) => v !== '' && v != null))} disabled={!data?.total} navigateAfter />}
      />
      <SearchPanel
        onSubmit={handleSubmit(({ sOnly, gOnly, ...v }) => setFilter({ ...v, hasSighting: sOnly ? 'true' : '', hasGearFinding: gOnly ? 'true' : '', page: 1 }))}
        onReset={() => {
          reset({ sOnly: false, gOnly: false });
          setFilter({});
        }}
        searching={isFetching}
        columns={5}
      >
        <FormField label="事業実施者">
          <Controller
            control={control}
            name="businessOperatorId"
            render={({ field }) => <BusinessOperatorSelect fiscalYear={year} value={field.value ? Number(field.value) : null} onChange={(v) => field.onChange(v ?? '')} width="full" />}
          />
        </FormField>
        <FormField label="船名">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="期間（から）">
          <DateField {...register('dateFrom')} />
        </FormField>
        <FormField label="期間（まで）">
          <DateField {...register('dateTo')} />
        </FormField>
        <FormField label="絞り込み">
          <span className="bs-checks">
            <Controller control={control} name="sOnly" render={({ field }) => <Checkbox label="視認有のみ" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
            <Controller control={control} name="gOnly" render={({ field }) => <Checkbox label="漁具有のみ" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
          </span>
        </FormField>
      </SearchPanel>
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <>
          <DataTable
            caption="定時連絡一覧"
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            loading={isPending}
            dense
            pagination={{ pageSize: PAGE_SIZE, page: filter.page ?? 1, total: data?.total ?? 0, onPageChange: (p) => setFilter({ ...filter, page: p }) }}
            onRowClick={(r) => navigate(yearPath(`fixed-reports/${r.id}`))}
            empty={<EmptyState compact icon={<CalendarClock />} title="該当する定時連絡はありません" description="検索条件を変更してください。" />}
            expandedKeys={[...open]}
            renderExpanded={(r) => <WorkSummary r={r} />}
          />
        </>
      )}
    </>
  );
}

/** 作業概要・作業予定（一覧の行の直下に展開表示） */
function WorkSummary({ r }: { r: FixedReportDto }) {
  return (
    <table className="bs-work-table">
      <thead>
        <tr>
          <th>作業時間</th>
          <th>調査海域</th>
          <th>発見漁具</th>
          <th>発見船舶</th>
          <th>その他</th>
        </tr>
      </thead>
      <tbody>
        {r.workSummaries.map((w, i) => (
          <tr key={i}>
            <td className="bs-num">
              {w.startTime ?? ''}～{w.endTime ?? ''}
            </td>
            <td>{w.surveyArea}</td>
            <td>{w.foundGear}</td>
            <td>{w.foundVessel}</td>
            <td>{w.other}</td>
          </tr>
        ))}
        {r.workPlans.map((w, i) => (
          <tr key={`p${i}`}>
            <td>
              <StatusBadge tone="info">作業予定</StatusBadge>
            </td>
            <td>{w.surveyArea}</td>
            <td colSpan={3}>{w.other}</td>
          </tr>
        ))}
        {!r.workSummaries.length && !r.workPlans.length && (
          <tr>
            <td colSpan={5} className="bs-cell-sub">
              作業概要・作業予定はありません
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
